import { useEffect, useMemo, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { SlidersHorizontal, X } from "lucide-react"
import FilterSection from "./FilterSection"
import MultiSelectChips from "./MultiSelectChips"
import FilterDrawer from "./FilterDrawer"

const emptyFilters = () => ({
    brands: [],
    parent_categories: [],
    subcategories: [],
    calibers: [],
    models: [],
    conditions: [],
})

const toArray = (value) => {
    if (Array.isArray(value)) return value
    if (value == null || value === "") return []
    return [value]
}

const AUCTIONS_PATH = "/auctions"

/**
 * Seed the sidebar with the current page's anchor selections. Brand / model /
 * category / subcategory pages carry their identity in `context` (and in the
 * URL path, not query params), so those dimensions are pre-selected from
 * `context`; everything else (and the auctions page, which has no anchor) comes
 * from the query-param `initial` filters.
 */
const normalizeInitial = (initial, context) => {
    const base = initial ?? {}
    const anchorSubcategories = toArray(context?.subcategories)
    // The merged Category control seeds from `parentCategory` (a single parent,
    // set by category pages) and/or `categories` (bare slugs, e.g. a commercial-
    // category brand facet page anchoring on its slug).
    // Union the anchor (path-encoded) categories with query-string category
    // refinements — e.g. a subcategory group riding alongside a commercial-
    // category brand facet must survive rather than be replaced by the anchor.
    const anchorCategories = [
        ...(context?.parentCategory ? [context.parentCategory] : []),
        ...toArray(context?.categories),
    ]
    return {
        brands: context?.brand ? [context.brand] : toArray(base.brands),
        parent_categories: [
            ...new Set([...anchorCategories, ...toArray(base.parent_categories)]),
        ],
        subcategories: anchorSubcategories.length
            ? anchorSubcategories
            : toArray(base.subcategories),
        calibers: toArray(base.calibers),
        models: context?.model ? [context.model] : toArray(base.models),
        conditions: toArray(base.conditions),
    }
}

// Strip a model facet prefix (`n:`/`p:`) to the bare slug used in the URL path.
const stripFacetPrefix = (value) => value.replace(/^[a-z]:/, "")

// The Category control is a flat namespace: subcategory groups carry an `sg:`
// prefix; everything else is a bare category slug. A bare slug can be a parent
// category, a commercial category, or both (same name, one entry). Which one it
// acts as is resolved against `published` and whether a brand is selected — a
// parent when chosen alone (→ /categories/{slug}), a commercial *brand facet*
// when chosen with a brand (→ /{brand}/{slug}). Groups are pure refinements.
const isGroupValue = (value) => value.startsWith("sg:")
const plainCategoryValues = (values) => values.filter((v) => !isGroupValue(v))

// Split a selection's bare category slugs into parent-page categories and
// commercial (brand-facet) categories. A commercial acts as a brand facet only
// when a brand is selected; otherwise a shared-name slug resolves to its parent.
const classifyCategories = (values, brandSelected, published) => {
    const plain = plainCategoryValues(values)
    const commercials = brandSelected
        ? plain.filter((s) => published.commercialCategories.has(s))
        : []
    const parents = plain.filter(
        (s) => published.parentCategories.has(s) && !commercials.includes(s)
    )
    return { parents, commercials }
}

/**
 * The brand-dimension path for a selection, consulting `published` (the sets of
 * brand slugs and brand-facet values that have a live published page) so we
 * never navigate to a page the server would only bounce back. A brand facet is a
 * model (`n:`/`p:`) or a commercial category (bare slug):
 *   - single brand + single facet, both live → the brand/facet page.
 *   - single brand + single facet, brand published but facet not → the brand
 *     page, with the facet riding along as a query refinement.
 *   - single brand alone, published → the brand page.
 *   - anything unpublished, or a multi-brand/multi-facet selection → auctions.
 */
const resolveBrandPath = (filters, published) => {
    const [brand] = filters.brands
    const brandPublished = brand != null && published.brands.has(brand)
    const { commercials } = classifyCategories(
        filters.parent_categories,
        true,
        published
    )
    const modelFacets = filters.models
    const facetCount = modelFacets.length + commercials.length

    if (filters.brands.length === 1 && facetCount === 1) {
        const isModel = modelFacets.length === 1
        // Models validate against published.models; commercial categories always
        // render on a published brand.
        const facetSlug = isModel
            ? stripFacetPrefix(modelFacets[0])
            : commercials[0]
        const facetPublished = isModel
            ? published.models.has(modelFacets[0])
            : true
        if (brandPublished && facetPublished) {
            return `/${brand}/${facetSlug}`
        }
        if (brandPublished) {
            return `/${brand}`
        }
        return AUCTIONS_PATH
    }
    if (filters.brands.length === 1 && facetCount === 0) {
        return brandPublished ? `/${brand}` : AUCTIONS_PATH
    }
    return AUCTIONS_PATH
}

/**
 * The category-dimension path for a selection: a parent/subcategory page when it
 * narrows to a single parent + single subcategory, the parent page for a single
 * parent alone, otherwise the auctions listing.
 */
const resolveCategoryPath = (filters, published) => {
    // Only a parent category (+ subcategory) defines the path; groups and
    // commercial categories ride along as query refinements.
    const { parents } = classifyCategories(
        filters.parent_categories,
        false,
        published
    )
    if (parents.length === 1 && filters.subcategories.length === 1) {
        return `/categories/${parents[0]}/${filters.subcategories[0]}`
    }
    if (parents.length === 1 && filters.subcategories.length === 0) {
        return `/categories/${parents[0]}`
    }
    return AUCTIONS_PATH
}

const sameSet = (a, b) =>
    a.length === b.length && a.every((value) => b.includes(value))

/**
 * Whether two filter states select the same path-defining dimensions (brand,
 * model, category, subcategory). Caliber / condition / subcategory groups are
 * pure refinements and never change the page, so they're excluded.
 */
const pathDimsEqual = (a, b) =>
    sameSet(a.brands, b.brands) &&
    sameSet(a.models, b.models) &&
    sameSet(
        plainCategoryValues(a.parent_categories),
        plainCategoryValues(b.parent_categories)
    ) &&
    sameSet(a.subcategories, b.subcategories)

/**
 * The canonical destination for a selection, decided against the page's
 * `anchor` (the selection the page arrived with).
 *
 * A single brand or single category maps to its own path-anchored page. A
 * brand+category mix has no single canonical URL, so rather than bounce to the
 * auctions list it stays on the current path-anchored page: the brand page
 * keeps the brand in its path (the category rides along as a query refinement)
 * and the category page keeps the category. The anchor decides which dimension
 * owns the path; with no anchor (the auctions / search page) the mix — like any
 * multi-select or cleared selection — falls back to the auctions listing.
 */
const resolvePathTarget = (filters, anchor, published) => {
    const brandSelected = filters.brands.length > 0 || filters.models.length > 0
    const { parents, commercials } = classifyCategories(
        filters.parent_categories,
        brandSelected,
        published
    )

    // A commercial category joins the brand dimension (brand facet) when a brand
    // is selected; otherwise the category dimension is parents + subcategories.
    const hasBrandDim =
        filters.brands.length > 0 ||
        filters.models.length > 0 ||
        commercials.length > 0
    const hasCatDim = parents.length > 0 || filters.subcategories.length > 0

    if (hasBrandDim && hasCatDim) {
        const anchorBrandSelected =
            anchor.brands.length > 0 || anchor.models.length > 0
        const { parents: anchorParents } = classifyCategories(
            anchor.parent_categories,
            anchorBrandSelected,
            published
        )
        const brandAnchored = anchorBrandSelected
        const catAnchored =
            anchorParents.length > 0 || anchor.subcategories.length > 0

        if (brandAnchored) {
            return resolveBrandPath(filters, published)
        }
        if (catAnchored) {
            return resolveCategoryPath(filters, published)
        }
        return AUCTIONS_PATH
    }

    if (hasBrandDim) {
        return resolveBrandPath(filters, published)
    }

    if (hasCatDim) {
        return resolveCategoryPath(filters, published)
    }

    return AUCTIONS_PATH
}

const SEARCH_PATH = "/search"

// Persists whether the mobile filter drawer is open, so it survives the remount
// a canonical-path redirect causes (selecting a brand/category navigates to a
// new page and remounts the sidebar). Restored on mount for mobile viewports;
// cleared when the user explicitly closes the drawer.
const DRAWER_OPEN_STORAGE_KEY = "fal:filters:drawer-open"

const readDrawerOpen = () => {
    try {
        return window.localStorage.getItem(DRAWER_OPEN_STORAGE_KEY) === "1"
    } catch {
        return false
    }
}

const writeDrawerOpen = (open) => {
    try {
        if (open) {
            window.localStorage.setItem(DRAWER_OPEN_STORAGE_KEY, "1")
        } else {
            window.localStorage.removeItem(DRAWER_OPEN_STORAGE_KEY)
        }
    } catch {
        // ignore (storage unavailable / private mode)
    }
}

const isMobileViewport = () => {
    try {
        return window.matchMedia("(max-width: 767px)").matches
    } catch {
        return false
    }
}

export default function FilterSidebar({ options, initial, context, query }) {
    // `options` is a deferred Inertia prop (~523 KB — it dominated the initial
    // document, and with it the collection-page LCP), so it is undefined on
    // first render and again briefly after each filter navigation. Hold the
    // last resolved value so a navigation doesn't flash the sidebar back to a
    // skeleton; the skeleton renders only before the first resolution, which
    // also keeps the inputs away until `publishedTargets` exists — selecting a
    // brand against an empty published set would mis-route to /auctions.
    const lastOptionsRef = useRef(null)
    if (options != null) {
        lastOptionsRef.current = options
    }
    const resolvedOptions = options ?? lastOptionsRef.current
    const optionsLoading = resolvedOptions == null

    // The seed is the page's anchor identity; captured once so a `preserveState`
    // reload (which re-runs render with fresh props but keeps local state) can't
    // shift it. A real navigation remounts the component and re-seeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const seed = useMemo(() => normalizeInitial(initial, context), [])
    const [filters, setFilters] = useState(seed)
    const anchorRef = useRef(seed)
    const [drawerOpen, setDrawerOpen] = useState(false)
    // Animate a user-initiated open/close, but not a post-redirect restore — that
    // one should snap open instantly (it was already open before navigating).
    const [drawerAnimate, setDrawerAnimate] = useState(true)

    const openDrawer = () => {
        setDrawerAnimate(true)
        setDrawerOpen(true)
        writeDrawerOpen(true)
    }

    const closeDrawer = () => {
        setDrawerAnimate(true)
        setDrawerOpen(false)
        writeDrawerOpen(false)
    }

    // Reopen on mount if the drawer was open before a filter selection navigated
    // to a published page and remounted the sidebar. Mobile only — the drawer is
    // never used on desktop, where the sidebar is always visible. Skip the slide
    // animation: it's a restore, not a fresh open.
    useEffect(() => {
        if (readDrawerOpen() && isMobileViewport()) {
            setDrawerAnimate(false)
            setDrawerOpen(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Routing metadata (brand / model / parent-category / commercial-category
    // slugs that have a live page). Sent un-narrowed by the server so the path
    // resolver can tell a real destination from one the server would bounce back.
    const published = useMemo(
        () => ({
            brands: new Set(resolvedOptions?.publishedTargets?.brands ?? []),
            models: new Set(resolvedOptions?.publishedTargets?.models ?? []),
            parentCategories: new Set(
                resolvedOptions?.publishedTargets?.parentCategories ?? []
            ),
            commercialCategories: new Set(
                resolvedOptions?.publishedTargets?.commercialCategories ?? []
            ),
        }),
        [resolvedOptions]
    )

    const activeBrands = filters.brands
    // Only selected parent categories scope the dependent Subcategory filter;
    // commercial-only categories and subcategory groups don't.
    const activeParents = useMemo(
        () =>
            plainCategoryValues(filters.parent_categories).filter((s) =>
                published.parentCategories.has(s)
            ),
        [filters.parent_categories, published]
    )

    const modelOptions = useMemo(() => {
        const all = resolvedOptions?.models ?? []
        if (activeBrands.length === 0) return []
        const set = new Set(activeBrands)
        return all.filter(
            (m) =>
                !m.brand_slugs ||
                m.brand_slugs.length === 0 ||
                m.brand_slugs.some((s) => set.has(s))
        )
    }, [resolvedOptions, activeBrands])

    const subcategoryOptions = useMemo(() => {
        const all = resolvedOptions?.subcategories ?? []
        if (activeParents.length === 0) return []
        const set = new Set(activeParents)
        return all.filter((s) => !s.parent_slug || set.has(s.parent_slug))
    }, [resolvedOptions, activeParents])

    // A section is shown when it has options to offer, OR when it already has a
    // selection — so the chosen chip stays visible and removable even after the
    // server narrows that dimension's options to nothing.
    const showBrand =
        (resolvedOptions?.brands ?? []).length > 0 || filters.brands.length > 0
    const showModel =
        (activeBrands.length > 0 && modelOptions.length > 0) ||
        filters.models.length > 0
    const showParentCategory =
        (resolvedOptions?.parent_categories ?? []).length > 0 ||
        filters.parent_categories.length > 0
    const showSubcategory =
        (activeParents.length > 0 && subcategoryOptions.length > 0) ||
        filters.subcategories.length > 0

    // Models are scoped to the selected brand(s) and subcategories to the
    // selected parent(s); changing the parent dimension drops the now-orphaned
    // child selection so it can't leak into a stale path target.
    const update = (key, value) =>
        setFilters((prev) => {
            const next = { ...prev, [key]: value }
            if (key === "brands") {
                next.models = []
            }
            // Drop orphaned subcategories only when the selected parent
            // categories actually change — toggling a commercial category or
            // subcategory group in the same control shouldn't wipe the pick.
            const parentsOf = (vals) =>
                plainCategoryValues(vals).filter((s) =>
                    published.parentCategories.has(s)
                )
            if (
                key === "parent_categories" &&
                !sameSet(parentsOf(prev.parent_categories), parentsOf(value))
            ) {
                next.subcategories = []
            }
            return next
        })

    const buildPayload = (next) => {
        const payload = {}
        if (next.brands.length) payload.brands = next.brands
        if (next.parent_categories.length)
            payload.parent_categories = next.parent_categories
        if (next.subcategories.length)
            payload.subcategories = next.subcategories
        if (next.calibers.length) payload.calibers = next.calibers
        if (next.models.length) payload.models = next.models
        if (next.conditions.length) payload.conditions = next.conditions
        return payload
    }

    // Query-string filters to carry when landing on a path-anchored page: the
    // full selection minus the dimensions the path itself encodes (brand[/model]
    // for a brand page, parent[/subcategory] for a category page). The auctions
    // list encodes nothing in its path, so it carries the whole selection.
    // Remove a path-encoded category slug from the carried category selection,
    // keeping every other Category pick (groups, other categories) in the query.
    const dropCategorySlug = (payload, slug) => {
        if (!payload.parent_categories) {
            return
        }
        const kept = payload.parent_categories.filter((v) => v !== slug)
        if (kept.length) {
            payload.parent_categories = kept
        } else {
            delete payload.parent_categories
        }
    }

    const queryFilters = (target, next) => {
        const payload = buildPayload(next)
        if (target === AUCTIONS_PATH) {
            return payload
        }
        const segments = target.split("/").filter(Boolean)
        if (target.startsWith("/categories/")) {
            // Path: /categories/{parent}[/{subcategory}]. Drop the parent slug it
            // encodes; keep group / other-category refinements in the query.
            dropCategorySlug(payload, segments[1])
            delete payload.subcategories
        } else {
            delete payload.brands
            // The facet lives in the path only on a `/{brand}/{facet}` page; on
            // the bare `/{brand}` page (an unpublished facet falling back to the
            // brand page) it rides in the query string. The facet is a model or a
            // commercial category — strip the path-encoded slug, keep the rest.
            if (segments.length >= 2) {
                delete payload.models
                dropCategorySlug(payload, segments[1])
            }
        }
        return payload
    }

    const activeCount = useMemo(
        () => Object.keys(buildPayload(filters)).length,
        [filters]
    )

    // Filters apply automatically: any change to `filters` triggers a debounced
    // navigation. The debounce coalesces rapid changes into a single request,
    // and the first render is skipped so the page isn't reloaded with the
    // filters it already arrived with.
    //
    // Three outcomes, decided against the page's anchor (`anchorRef`, the
    // selection the page arrived with):
    //   1. The selection still maps to the current page → refine in place. The
    //      dimensions the path already encodes stay in the path; everything else
    //      (caliber/condition refinements and any added cross-dimension, e.g. a
    //      category picked on a brand page) rides in the query string. On the
    //      auctions page the whole selection lives in the query string.
    //   2. A brand+category mix on a path-anchored page → stay put (same as 1):
    //      the anchored dimension owns the path, the other rides in the query.
    //   3. The selection narrows to a different brand/category page → navigate
    //      there with the non-path dimensions carried over; anything broader
    //      (multi-select or a cleared selection, and any mix on the anchorless
    //      auctions page) → the auctions list with the full selection.
    //
    // `filters` is the only dependency: it is local state preserved across the
    // `preserveState` reload, so it changes solely on user interaction. `context`
    // gets a fresh reference on every Inertia reload — depending on it would
    // re-fire the effect after each apply and loop forever.
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        const timeout = setTimeout(() => {
            const current = window.location.pathname
            const refineHere = (params, preserveState) =>
                router.get(current, params, {
                    preserveScroll: true,
                    preserveState,
                    replace: true,
                })

            // The search page is not path-anchored: every dimension lives in the
            // query string and the free-text `q` is carried alongside, so a
            // filter change always refines in place at /search.
            if (context?.mode === "search") {
                const payload = buildPayload(filters)
                if (query) {
                    payload.q = query
                }
                router.get(SEARCH_PATH, payload, {
                    preserveScroll: true,
                    preserveState: true,
                    replace: true,
                })
                return
            }

            const target = resolvePathTarget(
                filters,
                anchorRef.current,
                published
            )

            if (
                pathDimsEqual(filters, anchorRef.current) ||
                target === current
            ) {
                refineHere(
                    current === AUCTIONS_PATH
                        ? buildPayload(filters)
                        : queryFilters(current, filters),
                    true
                )
                return
            }

            if (target === AUCTIONS_PATH) {
                router.get(AUCTIONS_PATH, buildPayload(filters), {
                    preserveScroll: true,
                    preserveState: current === AUCTIONS_PATH,
                    replace: current === AUCTIONS_PATH,
                })
                return
            }

            router.get(target, queryFilters(target, filters), {
                preserveScroll: true,
                preserveState: false,
                replace: false,
            })
        }, 350)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    const clear = () => setFilters(emptyFilters())

    const filterBody = optionsLoading ? (
        <div className="flex flex-col gap-6 animate-pulse" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex flex-col gap-3">
                    <div className="w-1/3 h-4 bg-gray-200 rounded" />
                    <div className="w-full h-9 bg-gray-200 rounded" />
                </div>
            ))}
        </div>
    ) : (
        <>
            {showBrand && (
                <FilterSection title="Brand">
                    <MultiSelectChips
                        name="brands"
                        options={resolvedOptions?.brands ?? []}
                        selected={filters.brands}
                        onChange={(v) => update("brands", v)}
                        placeholder="Search brands..."
                        emptyLabel="No matching brands"
                    />
                </FilterSection>
            )}

            {showModel && (
                <FilterSection title="Model">
                    <MultiSelectChips
                        name="models"
                        options={modelOptions}
                        selected={filters.models}
                        onChange={(v) => update("models", v)}
                        placeholder="Search models..."
                        emptyLabel="No matching models"
                    />
                </FilterSection>
            )}

            {showParentCategory && (
                <FilterSection title="Category">
                    <MultiSelectChips
                        name="parent_categories"
                        options={resolvedOptions?.parent_categories ?? []}
                        selected={filters.parent_categories}
                        onChange={(v) => update("parent_categories", v)}
                        placeholder="Search categories..."
                        emptyLabel="No matching categories"
                    />
                </FilterSection>
            )}

            {showSubcategory && (
                <FilterSection title="Subcategory">
                    <MultiSelectChips
                        name="subcategories"
                        options={subcategoryOptions}
                        selected={filters.subcategories}
                        onChange={(v) => update("subcategories", v)}
                        placeholder="Search subcategories..."
                        emptyLabel="No matching subcategories"
                    />
                </FilterSection>
            )}

            <FilterSection title="Caliber">
                <MultiSelectChips
                    name="calibers"
                    options={resolvedOptions?.calibers ?? []}
                    selected={filters.calibers}
                    onChange={(v) => update("calibers", v)}
                    placeholder="Search calibers..."
                    emptyLabel="No matching calibers"
                />
            </FilterSection>

            <FilterSection title="Condition">
                <MultiSelectChips
                    name="conditions"
                    options={resolvedOptions?.conditions ?? []}
                    selected={filters.conditions}
                    onChange={(v) => update("conditions", v)}
                    placeholder="Search conditions..."
                    emptyLabel="No matching conditions"
                />
            </FilterSection>
        </>
    )

    const resetButton = activeCount > 0 && (
        <button
            type="button"
            onClick={clear}
            className="shrink-0 text-sm font-medium text-[#7a1820] transition hover:underline"
        >
            Reset
        </button>
    )

    return (
        <>
            <aside className="hidden overflow-auto w-full min-w-[300px] p-5 bg-white border border-gray-200 rounded-lg shadow-sm md:max-h-[90vh] md:block md:max-w-xs">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            Filters
                        </h2>
                        <p className="text-sm text-gray-500">
                            Refine your search
                        </p>
                    </div>
                    {resetButton}
                </div>
                {filterBody}
            </aside>

            <button
                type="button"
                onClick={openDrawer}
                aria-label="Filters"
                className="relative inline-flex items-center self-stretch justify-center px-3 text-gray-800 bg-white border rounded-md shrink-0 md:hidden hover:bg-gray-50"
            >
                <SlidersHorizontal className="w-5 h-5" />
                {activeCount > 0 && (
                    <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-semibold text-white rounded-full bg-[#7a1820]">
                        {activeCount}
                    </span>
                )}
            </button>

            <FilterDrawer
                open={drawerOpen}
                animate={drawerAnimate}
                onClose={closeDrawer}
            >
                <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4 shrink-0 border-b border-gray-200">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            Filters
                        </h2>
                        <p className="text-sm text-gray-500">
                            Refine your search
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {resetButton}
                        <button
                            type="button"
                            onClick={closeDrawer}
                            aria-label="Close filters"
                            className="p-1 -m-1 text-gray-500 transition rounded-full hover:bg-gray-100 hover:text-gray-900"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="flex-1 min-h-0 px-5 pb-5 overflow-y-auto overscroll-contain">
                    {filterBody}
                </div>
            </FilterDrawer>
        </>
    )
}
